// Eskom PARCC — Manage Transactions editor (admin add/edit form).
//
// The base-fields form behind the Manage screen, in two modes:
//   • Add (mode === 'add'): a blank form. Every field is editable, including the
//     insert-only identity fields (referenceNo/baseNo/recordLabel/year/
//     csdNumber). `referenceNo` is REQUIRED and must be UNIQUE — a friendly
//     pre-check (`isDuplicateRef`) blocks a collision before any write (the DB
//     also has a unique constraint, but a pre-check is better UX). On save it
//     builds the FULL insert payload and `useUpsertTransaction` inserts.
//   • Edit (mode === 'edit'): seeded from the selected transaction. Only the
//     writable-UPDATE subset is editable; the insert-only identity fields are
//     rendered READ-ONLY (the UpdateTransactionData mutation cannot change them —
//     see dbWrite). `period` left that group in Aug 2026 (owner feedback:
//     unable to update period) — editable in both modes, rides the update payload.
//     Save runs `checkRowVersion` (frozen at edit-open) FIRST and
//     aborts on conflict, then builds the update payload and upserts.
//
// Admin-only: the route gates the screen, and the parent re-confirms the admin
// role before mounting the editor in a write context; saves go through the
// shared `useUpsertTransaction` (which owns its own error toast + cache
// invalidation). Explicit Save only; disabled in flight.
import { useMemo, useState } from 'react';
import { Card, Button, Select, useToast } from '../../components/ui';
import {
  DATASET_OPTIONS,
  DIVISION_OPTIONS,
  MECHANISM_OPTIONS,
  ALL_COMPLIANCE_LEVELS,
  GAP_STATUS_OPTIONS,
  REVIEW_STATUS_OPTIONS,
} from '../../domain/enums';
import { linkState } from '../../domain/links';
import { checkRowVersion } from '../../lib/concurrency';
import { adminApi } from '../../lib/adminApi';
import { normaliseFinancialYear } from '../../domain/fiscal';
import { useUpsertTransaction, useSoftDeleteTransaction, useRestoreTransaction } from '../../hooks';
import { diffFields, changesNote, TRANSACTION_FIELD_LABELS } from '../../lib/fieldDiff';
import type { Transaction } from '../../data/types';
import {
  type ManageForm,
  EMPTY_MANAGE_FORM,
  transactionToForm,
  buildManageInsert,
  buildManageUpdate,
  isDuplicateRef,
  findRemovedByRef,
} from './manageTransaction';

const PERIOD_OPTIONS = ['Q1', 'Q2', 'Q3', 'Q4'] as const;

export type ManageMode = 'add' | 'edit';

export function ManageEditor({
  mode,
  tx,
  transactions,
  isAdmin,
  onClose,
}: {
  mode: ManageMode;
  /** The transaction under edit; absent in Add mode. */
  tx?: Transaction;
  /** The full transaction population, for the uniqueness pre-check on Add. */
  transactions: readonly Transaction[];
  /** Re-confirmed admin flag (defence-in-depth alongside the route gate). */
  isAdmin: boolean;
  /** Leave the editor (after a successful save, or on cancel). */
  onClose: () => void;
}): React.JSX.Element {
  const { show } = useToast();
  const upsertTransaction = useUpsertTransaction();
  const softDeleteTransaction = useSoftDeleteTransaction();
  const restoreTransaction = useRestoreTransaction();

  const [form, setForm] = useState<ManageForm>(() =>
    mode === 'edit' && tx ? transactionToForm(tx) : EMPTY_MANAGE_FORM,
  );

  // Freeze the rowVersion the editor opened against — the optimistic-concurrency
  // precondition checked at Save time (Edit only). Captured once at mount so a
  // background refetch advancing the SAME open transaction's rowVersion cannot
  // silently track forward and defeat the conflict guard (see TransactionEditor).
  const [loadedRowVersion] = useState(() => tx?.rowVersion ?? 0);

  const deleting = softDeleteTransaction.isPending;
  const restoring = restoreTransaction.isPending;
  const saving = upsertTransaction.isPending || deleting || restoring;

  function set<K extends keyof ManageForm>(key: K, value: ManageForm[K]): void {
    setForm((f) => ({ ...f, [key]: value }));
  }

  // Pre-compute the Add-mode duplicate flag for live feedback on the ref field.
  const refTrimmed = form.referenceNo.trim();
  const duplicate = useMemo(
    () => mode === 'add' && refTrimmed !== '' && isDuplicateRef(refTrimmed, transactions),
    [mode, refTrimmed, transactions],
  );
  // A collision specifically against a REMOVED row → tell the admin to restore it
  // rather than "already exists" (Lindiwe R4 #6: the silent false-success case).
  const removedMatch = useMemo(
    () =>
      mode === 'add' && refTrimmed !== '' ? findRemovedByRef(refTrimmed, transactions) : undefined,
    [mode, refTrimmed, transactions],
  );

  async function handleSave(): Promise<void> {
    // Re-confirm the admin role in the handler — not just at the route — so a
    // write can never be reached by a non-admin even if the route gate regresses.
    if (!isAdmin) {
      show('Only administrators may manage transactions.', 'error');
      return;
    }

    if (mode === 'add') {
      // Required + unique reference, checked before any write (the DB constraint
      // is the backstop; this is the friendly front-stop).
      if (refTrimmed === '') {
        show('A reference number is required.', 'error');
        return;
      }
      // A removed twin: adding would silently update the hidden row (the R4 #6 bug).
      // Point the admin at Restore instead of a misleading "already exists".
      if (removedMatch) {
        show(
          `Reference "${refTrimmed}" belongs to a removed transaction. Restore it from the list instead of adding it again.`,
          'error',
        );
        return;
      }
      if (isDuplicateRef(refTrimmed, transactions)) {
        show(`A transaction with reference "${refTrimmed}" already exists.`, 'error');
        return;
      }
      try {
        // A create: no prior row to diff against, so the insert_transaction stamp
        // stands alone (the new values live on the record itself).
        await upsertTransaction.mutateAsync({ input: buildManageInsert(form) });
      } catch {
        // Already toasted by the hook's onError; keep the editor open for a retry.
        return;
      }
      show('Transaction added.', 'success');
      onClose();
      return;
    }

    // Edit mode. Concurrency guard FIRST — narrow the last-write-wins window. On
    // conflict, don't write the stale edit; tell the user and pull the fresh
    // state. This runs before any mutation, so a throw here means nothing was
    // written; surface it here (the mutation hook is not involved yet).
    if (!tx) return; // defensive: edit mode always carries a tx
    let check;
    try {
      check = await checkRowVersion(tx.referenceNo, loadedRowVersion);
    } catch (error) {
      show(
        error instanceof Error ? error.message : 'Could not verify the transaction. Please retry.',
        'error',
      );
      return;
    }
    if (!check.ok) {
      show(
        check.current === null
          ? 'This transaction no longer exists. Refreshing…'
          : 'This transaction was changed by someone else. Refreshing — please reapply your edits.',
        'error',
      );
      onClose(); // re-reads from the (invalidated/fresh) cache
      return;
    }

    try {
      const txInput = buildManageUpdate(form);
      const txChanges = diffFields(
        tx as unknown as Record<string, unknown>,
        txInput as unknown as Record<string, unknown>,
        TRANSACTION_FIELD_LABELS,
      );
      await upsertTransaction.mutateAsync({
        input: txInput,
        audit: { note: changesNote(txChanges), changes: txChanges },
      });
    } catch {
      // Already toasted by the hook's onError; keep the editor open for a retry.
      return;
    }
    // `year` is INSERT-ONLY at the connector, so buildManageUpdate omits it and the
    // upsert above never wrote it. When the reviewer changes the financial year,
    // persist it through the admin service (Lindiwe, 16 Sep 2026), which audits it.
    // Normalise + validate here so a typo surfaces immediately, and send the canonical
    // form ("F27" → "FY27") the server expects. A no-op (same canonical FY) is skipped.
    if (String(form.year ?? '').trim() !== String(tx.year ?? '').trim()) {
      const newYear = normaliseFinancialYear(form.year);
      if (!newYear) {
        show(`"${form.year}" is not a recognisable financial year (e.g. FY27).`, 'error');
        return; // keep the editor open so the reviewer can fix the value
      }
      if (newYear !== normaliseFinancialYear(tx.year)) {
        try {
          await adminApi.setTransactionYear(tx.referenceNo, newYear);
        } catch (error) {
          show(
            error instanceof Error ? error.message : 'The financial year could not be saved.',
            'error',
          );
          return; // keep the editor open so the reviewer can retry
        }
      }
    }
    show('Transaction updated.', 'success');
    onClose();
  }

  /**
   * Soft-delete the transaction under edit (admin-only, edit-mode only). Confirms
   * first (a destructive, if reversible, action), then runs the soft-delete hook
   * which flips `deleted` and best-effort-audits. On success the list refetches
   * (the soft-deleted row drops out) and the editor closes.
   */
  async function handleDelete(): Promise<void> {
    if (!isAdmin) {
      show('Only administrators may delete transactions.', 'error');
      return;
    }
    if (mode !== 'edit' || !tx) return; // delete is edit-mode only
    const confirmed = window.confirm(
      `Delete transaction "${tx.referenceNo}"? It will be removed from the register (soft-delete).`,
    );
    if (!confirmed) return;
    try {
      await softDeleteTransaction.mutateAsync(tx.referenceNo);
    } catch {
      // Already toasted by the hook's onError; keep the editor open for a retry.
      return;
    }
    show('Transaction deleted.', 'success');
    onClose();
  }

  /**
   * Restore a removed (soft-deleted) transaction under edit (admin-only). `fresh`
   * also clears its existing findings so the record starts a clean review cycle —
   * the case Lindiwe hit (a removed reference still carried its test findings).
   */
  async function handleRestore(fresh: boolean): Promise<void> {
    if (!isAdmin) {
      show('Only administrators may restore transactions.', 'error');
      return;
    }
    if (mode !== 'edit' || !tx) return;
    const confirmed = window.confirm(
      fresh
        ? `Restore "${tx.referenceNo}" as a fresh transaction? Its existing findings will be removed and the review starts again from scratch.`
        : `Restore "${tx.referenceNo}" with its history? Its findings and working paper carry forward, and the review status resets to Not Reviewed.`,
    );
    if (!confirmed) return;
    try {
      await restoreTransaction.mutateAsync({ referenceNo: tx.referenceNo, fresh });
    } catch {
      return; // toasted by the hook's onError; keep the panel open for a retry
    }
    show(
      fresh ? 'Transaction restored as a fresh record.' : 'Transaction restored with its history.',
      'success',
    );
    onClose();
  }

  // Editing a REMOVED transaction: offer restore, not the base-fields form. A
  // removed row cannot be edited in place — it must come back first.
  if (mode === 'edit' && tx?.deleted) {
    return (
      <Card>
        <div className="summarybox">
          <div>
            <div style={{ fontSize: 20, fontWeight: 800 }}>{tx.referenceNo}</div>
            <div className="small">This transaction was removed from the register.</div>
          </div>
          <div className="actions">
            <Button variant="ghost" onClick={onClose} disabled={saving}>
              Cancel
            </Button>
          </div>
        </div>
        <div className="modal-grid" style={{ marginTop: 14 }}>
          <p className="small">
            Restore it to make it live again. <b>With history</b> keeps its captured findings and
            working paper (review status resets to Not Reviewed). <b>Fresh</b> removes its existing
            findings so the review starts from scratch — use this if the removed record still
            carries test-review findings.
          </p>
          <div className="actions" style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <Button variant="primary" onClick={() => void handleRestore(false)} disabled={saving}>
              {restoring ? 'Restoring…' : 'Restore with history'}
            </Button>
            <Button onClick={() => void handleRestore(true)} disabled={saving}>
              {restoring ? 'Restoring…' : 'Restore fresh'}
            </Button>
          </div>
        </div>
      </Card>
    );
  }

  const heading = mode === 'add' ? 'New transaction' : form.referenceNo;
  const subhead =
    mode === 'add'
      ? 'Capture the base fields for a new transaction'
      : 'Editing base fields — identity fields are fixed once created';

  return (
    <Card>
      <div className="summarybox">
        <div>
          <div style={{ fontSize: 20, fontWeight: 800 }}>{heading || 'New transaction'}</div>
          <div className="small">{subhead}</div>
        </div>
        <div className="actions">
          <Button variant="ghost" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={() => void handleSave()}
            disabled={saving || (mode === 'add' && (refTrimmed === '' || duplicate))}
          >
            {saving ? 'Saving…' : mode === 'add' ? 'Add transaction' : 'Save changes'}
          </Button>
        </div>
      </div>

      <div className="modal-grid" style={{ marginTop: 14 }}>
        {/* Identity fields — editable on Add, READ-ONLY on Edit (the connector
            cannot change them; see dbWrite TransactionUpdateInput). */}
        <label>
          Reference No{mode === 'add' ? ' *' : ''}
          <input
            name="referenceNo"
            value={form.referenceNo}
            readOnly={mode === 'edit'}
            disabled={saving}
            onChange={(e) => set('referenceNo', e.target.value)}
            placeholder="e.g. C-001"
            aria-invalid={duplicate || undefined}
          />
          {mode === 'add' && duplicate && (
            <span className="small" style={{ color: 'var(--danger, #ff6b6b)' }}>
              {removedMatch
                ? 'This reference belongs to a removed transaction — restore it from the list instead.'
                : 'A transaction with this reference already exists.'}
            </span>
          )}
          {mode === 'edit' && (
            <span className="small">The reference cannot be changed once created.</span>
          )}
        </label>
        <label>
          Base No
          <input
            name="baseNo"
            value={form.baseNo}
            readOnly={mode === 'edit'}
            disabled={saving}
            onChange={(e) => set('baseNo', e.target.value)}
          />
        </label>
        <label>
          Record Label
          <input
            name="recordLabel"
            value={form.recordLabel}
            readOnly={mode === 'edit'}
            disabled={saving}
            onChange={(e) => set('recordLabel', e.target.value)}
          />
        </label>
        <label>
          Year
          <input
            name="year"
            value={form.year}
            readOnly={mode === 'edit'}
            disabled={saving}
            onChange={(e) => set('year', e.target.value)}
          />
        </label>
        {/* Period is editable in BOTH modes (owner feedback, Aug 2026) — the connector
            updates it, unlike its identity-field neighbours. A blank selection
            leaves the stored period unchanged rather than clearing it. */}
        <label>
          Period
          <Select
            name="period"
            ariaLabel="Period"
            value={form.period}
            disabled={saving}
            onChange={(v) => set('period', v)}
            options={[
              { value: '', label: 'Select period' },
              ...PERIOD_OPTIONS.map((o) => ({ value: o, label: o })),
              // A stored period outside Q1–Q4 (imports accept free text) must
              // still render — and survive an untouched save — rather than
              // masquerade as unset.
              ...(form.period !== '' && !(PERIOD_OPTIONS as readonly string[]).includes(form.period)
                ? [{ value: form.period, label: form.period }]
                : []),
            ]}
          />
        </label>
        <label>
          CSD Number
          <input
            name="csdNumber"
            value={form.csdNumber}
            readOnly={mode === 'edit'}
            disabled={saving}
            onChange={(e) => set('csdNumber', e.target.value)}
          />
        </label>

        {/* Writable base fields — editable in both modes. */}
        <label>
          Dataset
          <Select
            name="dataset"
            ariaLabel="Dataset"
            value={form.dataset}
            disabled={saving}
            onChange={(v) => set('dataset', v)}
            options={[
              { value: '', label: 'Select dataset' },
              ...DATASET_OPTIONS.map((o) => ({ value: o, label: o })),
            ]}
          />
        </label>
        <label>
          Division
          <Select
            name="division"
            ariaLabel="Division"
            value={form.division}
            disabled={saving}
            onChange={(v) => set('division', v)}
            options={[
              { value: '', label: 'Select division' },
              ...DIVISION_OPTIONS.map((o) => ({ value: o, label: o })),
            ]}
          />
        </label>
        <label className="full">
          Supplier
          <input
            name="supplier"
            value={form.supplier}
            disabled={saving}
            onChange={(e) => set('supplier', e.target.value)}
          />
        </label>
        <label>
          Value (R)
          <input
            name="value"
            value={form.value}
            disabled={saving}
            onChange={(e) => set('value', e.target.value)}
            placeholder="e.g. 1 234 567"
            inputMode="decimal"
          />
        </label>
        <label>
          Mechanism
          <Select
            name="mechanism"
            ariaLabel="Mechanism"
            value={form.mechanism}
            disabled={saving}
            onChange={(v) => set('mechanism', v)}
            options={[
              { value: '', label: 'Select mechanism' },
              ...MECHANISM_OPTIONS.map((o) => ({ value: o, label: o })),
            ]}
          />
        </label>
        <label>
          Gaps Status
          <Select
            name="gapsStatus"
            ariaLabel="Gaps Status"
            value={form.gapsStatus}
            disabled={saving}
            onChange={(v) => set('gapsStatus', v)}
            options={[
              { value: '', label: 'Select status' },
              ...GAP_STATUS_OPTIONS.map((o) => ({ value: o, label: o })),
            ]}
          />
        </label>
        <label>
          Review Status
          <Select
            name="reviewStatus"
            ariaLabel="Review Status"
            value={form.reviewStatus}
            disabled={saving}
            onChange={(v) => set('reviewStatus', v)}
            options={[
              { value: '', label: 'Select status' },
              ...REVIEW_STATUS_OPTIONS.map((o) => ({ value: o, label: o })),
            ]}
          />
        </label>
        <label>
          Compliance Level
          <Select
            name="complianceLevel"
            ariaLabel="Compliance Level"
            value={form.complianceLevel}
            disabled={saving}
            onChange={(v) => set('complianceLevel', v)}
            options={[
              { value: '', label: 'Use suggested result from findings' },
              ...ALL_COMPLIANCE_LEVELS.map((o) => ({ value: o, label: o })),
            ]}
          />
        </label>
        <label>
          Compliance Override
          <Select
            name="complianceLevelOverride"
            ariaLabel="Compliance Override"
            value={form.complianceLevelOverride}
            disabled={saving}
            onChange={(v) => set('complianceLevelOverride', v)}
            options={[
              { value: '', label: 'No override' },
              ...ALL_COMPLIANCE_LEVELS.map((o) => ({ value: o, label: o })),
            ]}
          />
        </label>
        <label className="full">
          Self-declaration Listing
          <input
            name="selfDeclaration"
            value={form.selfDeclaration}
            disabled={saving}
            onChange={(e) => set('selfDeclaration', e.target.value)}
          />
        </label>
        <label className="full">
          Link
          <input
            name="link"
            value={form.link}
            disabled={saving}
            onChange={(e) => set('link', e.target.value)}
          />
        </label>
        {/* Owner feedback #8: the link STATUS (provided flag + issue) is editable
            here, not only via a spreadsheet upload. The derived Received/Pending/
            Outstanding state updates live so the coordinator sees the effect. */}
        <label>
          Link provided
          <Select
            name="linkProvided"
            ariaLabel="Link provided"
            value={form.linkProvided}
            disabled={saving}
            onChange={(v) => set('linkProvided', v)}
            options={[
              { value: '', label: 'Not set' },
              { value: 'Yes', label: 'Yes' },
              { value: 'No', label: 'No' },
            ]}
          />
        </label>
        <label>
          Issue with links
          <input
            name="linkIssue"
            value={form.linkIssue}
            disabled={saving}
            placeholder="No"
            onChange={(e) => set('linkIssue', e.target.value)}
          />
        </label>
        <label className="full manage-link-status">
          Link status
          <output>
            {linkState({ link: form.link, linkProvided: form.linkProvided === 'Yes' })}
          </output>
        </label>
      </div>

      <div className="manage-sticky-actions">
        {/* Soft-delete — admin-only, edit mode only (a transaction must exist).
            Confirms first, then flips `deleted` so the row leaves the register. */}
        {mode === 'edit' && tx && (
          <Button
            variant="ghost"
            onClick={() => void handleDelete()}
            disabled={saving || !isAdmin}
            title="Soft-delete this transaction (removes it from the register)."
          >
            {deleting ? 'Deleting…' : 'Delete'}
          </Button>
        )}
        <Button variant="ghost" onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={() => void handleSave()}
          disabled={saving || (mode === 'add' && (refTrimmed === '' || duplicate))}
        >
          {saving ? 'Saving…' : mode === 'add' ? 'Add transaction' : 'Save changes'}
        </Button>
      </div>
    </Card>
  );
}
